import type { ColumnDef, OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import * as React from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { ButtonWithIcon } from "@/components/ui/button-with-icon";
import { DataTable } from "@/components/ui/data-table";
import { createDataTableSelectColumn } from "@/components/ui/data-table-select-column";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TableOutlineDeleteIconButton } from "@/components/ui/table-outline-delete-button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  METRIC_TOOLTIP_ARROW_CLASS_NAME,
  METRIC_TOOLTIP_BODY_CLASS,
  metricTooltipShellClassName,
} from "@/components/ui/metric-tooltip";
import { TRADE_LOG_FILTER_INPUT_CLASS } from "@/components/trade-filter-selects";
import { cn } from "@/lib/utils";
import { ArchiveIcon, type ArchiveIconHandle } from "@/components/ui/archive-icon";
import { UndoIcon, type UndoIconHandle } from "@/components/ui/undo-icon";
import type { ReportStrategyResponse } from "../../../types/reports";
import { strategyHasMasterPinescriptUploaded } from "../../../utils/reportStrategyPineUi";
import {
  StrategyTableMasterPineBadge,
  StrategyTableTradingViewPineBadge,
} from "./strategyTablePineBadges";

function fmtUtcLike(s: unknown): string {
  if (typeof s !== "string" || !s.trim()) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().replace("T", " ").slice(0, 19) + "Z";
}

function StrategyTableDescriptionCell({
  description,
}: {
  description?: string | null;
}): React.ReactElement {
  const trimmed = description?.trim() ?? "";
  const mutedClassName =
    "max-w-[360px] truncate font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white";

  if (!trimmed) {
    return (
      <p data-strategies-table-muted="" className={mutedClassName}>
        —
      </p>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <p data-strategies-table-muted="" className={cn(mutedClassName, "cursor-default")}>
          {trimmed}
        </p>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={4}
        arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
        className={metricTooltipShellClassName("max-w-[360px]")}
        data-strategies-table-description-tooltip=""
      >
        <p
          className={cn(
            "whitespace-pre-wrap text-left text-sm font-normal leading-relaxed",
            METRIC_TOOLTIP_BODY_CLASS,
          )}
        >
          {trimmed}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

export type StrategyListTab = "saved" | "archived";

export type StrategiesTableProps = {
  savedRows: ReportStrategyResponse[];
  archivedRows: ReportStrategyResponse[];
  listTab: StrategyListTab;
  onListTabChange: (tab: StrategyListTab) => void;
  onOpenStrategy: (strategy: ReportStrategyResponse) => void;
  query: string;
  onQueryChange: (value: string) => void;
  onNewStrategy: () => void;
  onArchiveStrategy: (strategyId: string) => void;
  onRestoreStrategy: (strategyId: string) => void;
  onDeleteStrategy: (strategy: ReportStrategyResponse) => void;
  onBulkArchive: (strategyIds: string[]) => Promise<string[]>;
  onBulkRestore: (strategyIds: string[]) => Promise<string[]>;
  onBulkDelete: (strategies: ReportStrategyResponse[]) => Promise<string[]>;
  bulkActionBusy: boolean;
  archivingStrategyId: string | null;
  restoringStrategyId: string | null;
  deletingStrategyId: string | null;
};

function StrategyTablePanel(props: {
  mode: StrategyListTab;
  rows: ReportStrategyResponse[];
  onOpenStrategy: (strategy: ReportStrategyResponse) => void;
  columns: ColumnDef<ReportStrategyResponse>[];
  emptyMessage: string;
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
}): React.ReactElement {
  return (
    <div
      data-strategies-list-table=""
      data-trade-log-trades-table=""
      className="flex w-full min-w-0 flex-col gap-4"
    >
      <DataTable
        columns={props.columns}
        data={props.rows}
        getRowId={(row) => row.id}
        onRowClick={(row) => props.onOpenStrategy(row)}
        pagination={{ pageIndex: 0, pageSize: 25 }}
        showPagination={props.rows.length > 25}
        emptyMessage={props.emptyMessage}
        enableRowSelection
        rowSelection={props.rowSelection}
        onRowSelectionChange={props.onRowSelectionChange}
        tableClassName="dark:!bg-sidebar"
      />
    </div>
  );
}

function selectedRowsFromSelection(
  rows: ReportStrategyResponse[],
  rowSelection: RowSelectionState,
): ReportStrategyResponse[] {
  const selectedIds = new Set(
    Object.entries(rowSelection)
      .filter(([, selected]) => selected)
      .map(([id]) => id),
  );
  if (selectedIds.size === 0) return [];
  return rows.filter((row) => selectedIds.has(row.id));
}

function removeIdsFromRowSelection(
  rowSelection: RowSelectionState,
  ids: string[],
): RowSelectionState {
  if (ids.length === 0) return rowSelection;
  const next = { ...rowSelection };
  for (const id of ids) {
    delete next[id];
  }
  return next;
}

function selectedIdsFromSelection(rowSelection: RowSelectionState): string[] {
  return Object.entries(rowSelection)
    .filter(([, selected]) => selected)
    .map(([id]) => id);
}

function StrategyTableArchiveButton({
  isArchiving,
  disabled,
  onArchive,
}: {
  isArchiving: boolean;
  disabled: boolean;
  onArchive: () => void;
}): React.ReactElement {
  const archiveIconRef = useRef<ArchiveIconHandle | null>(null);

  const startArchiveIconAnimation = useCallback(() => {
    void archiveIconRef.current?.startAnimation();
  }, []);

  const stopArchiveIconAnimation = useCallback(() => {
    void archiveIconRef.current?.stopAnimation();
  }, []);

  const archiveIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startArchiveIconAnimation,
      onMouseEnter: startArchiveIconAnimation,
      onMouseLeave: stopArchiveIconAnimation,
    }),
    [startArchiveIconAnimation, stopArchiveIconAnimation]
  );

  return (
    <ButtonWithIcon
      type="button"
      variant="outline-orange"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onArchive();
      }}
      label={isArchiving ? "Archiving…" : "Archive"}
      icon={
        isArchiving ? undefined : (
          <ArchiveIcon
            ref={archiveIconRef}
            data-icon="inline-start"
            size={16}
            className="inline-flex shrink-0 items-center text-current"
            aria-hidden
          />
        )
      }
      {...archiveIconHoverHandlers}
    />
  );
}

function StrategyTableRestoreButton({
  isRestoring,
  disabled,
  onRestore,
}: {
  isRestoring: boolean;
  disabled: boolean;
  onRestore: () => void;
}): React.ReactElement {
  const undoIconRef = useRef<UndoIconHandle | null>(null);

  const startUndoIconAnimation = useCallback(() => {
    void undoIconRef.current?.startAnimation();
  }, []);

  const stopUndoIconAnimation = useCallback(() => {
    void undoIconRef.current?.stopAnimation();
  }, []);

  const undoIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startUndoIconAnimation,
      onMouseEnter: startUndoIconAnimation,
      onMouseLeave: stopUndoIconAnimation,
    }),
    [startUndoIconAnimation, stopUndoIconAnimation]
  );

  return (
    <ButtonWithIcon
      type="button"
      variant="outline-brand"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onRestore();
      }}
      label={isRestoring ? "Restoring…" : "Undo"}
      icon={
        isRestoring ? undefined : (
          <UndoIcon
            ref={undoIconRef}
            data-icon="inline-start"
            size={16}
            className="inline-flex shrink-0 items-center text-current"
            aria-hidden
          />
        )
      }
      {...undoIconHoverHandlers}
    />
  );
}

export function StrategiesTable(props: StrategiesTableProps): React.ReactElement {
  const [savedRowSelection, setSavedRowSelection] = useState<RowSelectionState>({});
  const [archivedRowSelection, setArchivedRowSelection] = useState<RowSelectionState>({});

  const handleListTabChange = useCallback(
    (tab: StrategyListTab) => {
      props.onListTabChange(tab);
      setSavedRowSelection({});
      setArchivedRowSelection({});
    },
    [props.onListTabChange],
  );

  const buildColumns = useMemo(() => {
    return (mode: StrategyListTab): ColumnDef<ReportStrategyResponse>[] => [
      createDataTableSelectColumn<ReportStrategyResponse>(),
      {
        id: "name",
        accessorFn: (row) => row.name ?? "",
        header: "Name",
        cell: ({ row }) => {
          const strategy = row.original;
          const fillCount =
            typeof strategy.research_sim_fill_count === "number"
              ? Math.round(strategy.research_sim_fill_count)
              : null;
          return (
            <div data-strategies-table-name="" className="min-w-0 max-w-[280px]">
              <p
                data-strategies-table-name=""
                className="truncate font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)] dark:text-white"
                title={strategy.name}
              >
                {strategy.name}
              </p>
              {strategy.has_research_sim ? (
                <p
                  data-strategy-research-sim-badge=""
                  className="mt-0.5 truncate font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-sidebar-foreground"
                >
                  {fillCount != null && fillCount > 0
                    ? `Research sim · ${fillCount} fills`
                    : "Research sim"}
                </p>
              ) : null}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "description",
        accessorFn: (row) => row.description ?? "",
        header: "Description",
        cell: ({ row }) => (
          <StrategyTableDescriptionCell description={row.original.description} />
        ),
        enableSorting: false,
      },
      {
        id: "master-pine",
        accessorFn: (row) =>
          strategyHasMasterPinescriptUploaded(row) ? "uploaded" : "not-uploaded",
        header: "Master Pinescript",
        cell: ({ row }) => <StrategyTableMasterPineBadge strategy={row.original} />,
        enableSorting: false,
      },
      {
        id: "tradingview-pine",
        accessorFn: (row) => row.active_pine?.status ?? "",
        header: "TradingView Pinescript",
        cell: ({ row }) => <StrategyTableTradingViewPineBadge strategy={row.original} />,
        enableSorting: false,
      },
      {
        id: "updated",
        accessorFn: (row) => row.updated_at ?? "",
        header: ({ column }) => (
          <DataTableColumnHeader<ReportStrategyResponse, unknown> column={column} title="Updated" />
        ),
        cell: ({ row }) => (
          <p
            data-strategies-table-muted=""
            className="whitespace-nowrap font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white"
          >
            {fmtUtcLike(row.original.updated_at)}
          </p>
        ),
      },
      {
        id: "actions",
        size: mode === "saved" ? 160 : 140,
        header: "Actions",
        cell: ({ row }) => {
          const strategy = row.original;
          const isArchiving = props.archivingStrategyId === strategy.id;
          const isRestoring = props.restoringStrategyId === strategy.id;
          const isDeleting = props.deletingStrategyId === strategy.id;
          const actionBusy = isArchiving || isRestoring || isDeleting;

          return (
            <div className="flex items-center gap-2">
              {mode === "saved" ? (
                <StrategyTableArchiveButton
                  isArchiving={isArchiving}
                  disabled={actionBusy}
                  onArchive={() => props.onArchiveStrategy(strategy.id)}
                />
              ) : (
                <StrategyTableRestoreButton
                  isRestoring={isRestoring}
                  disabled={actionBusy}
                  onRestore={() => props.onRestoreStrategy(strategy.id)}
                />
              )}
              <TableOutlineDeleteIconButton
                marker="strategies"
                aria-label={`Delete ${strategy.name}`}
                disabled={actionBusy}
                onClick={() => props.onDeleteStrategy(strategy)}
              />
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
    ];
  }, [
    props.archivingStrategyId,
    props.deletingStrategyId,
    props.onArchiveStrategy,
    props.onDeleteStrategy,
    props.onRestoreStrategy,
    props.restoringStrategyId,
  ]);

  const savedColumns = useMemo(() => buildColumns("saved"), [buildColumns]);
  const archivedColumns = useMemo(() => buildColumns("archived"), [buildColumns]);

  const savedSelectedIds = useMemo(
    () => selectedIdsFromSelection(savedRowSelection),
    [savedRowSelection],
  );
  const archivedSelectedIds = useMemo(
    () => selectedIdsFromSelection(archivedRowSelection),
    [archivedRowSelection],
  );
  const savedSelectedRows = useMemo(
    () => selectedRowsFromSelection(props.savedRows, savedRowSelection),
    [props.savedRows, savedRowSelection],
  );
  const archivedSelectedRows = useMemo(
    () => selectedRowsFromSelection(props.archivedRows, archivedRowSelection),
    [props.archivedRows, archivedRowSelection],
  );

  const runBulkArchive = useCallback(async () => {
    if (savedSelectedIds.length === 0) return;
    const archivedIds = await props.onBulkArchive(savedSelectedIds);
    if (archivedIds.length > 0) {
      setSavedRowSelection((prev) => removeIdsFromRowSelection(prev, archivedIds));
    }
  }, [props, savedSelectedIds]);

  const runBulkRestore = useCallback(async () => {
    if (archivedSelectedIds.length === 0) return;
    const restoredIds = await props.onBulkRestore(archivedSelectedIds);
    if (restoredIds.length > 0) {
      setArchivedRowSelection((prev) => removeIdsFromRowSelection(prev, restoredIds));
    }
  }, [archivedSelectedIds, props]);

  const runBulkDeleteSaved = useCallback(async () => {
    if (savedSelectedRows.length === 0) return;
    const deletedIds = await props.onBulkDelete(savedSelectedRows);
    if (deletedIds.length > 0) {
      setSavedRowSelection((prev) => removeIdsFromRowSelection(prev, deletedIds));
    }
  }, [props, savedSelectedRows]);

  const runBulkDeleteArchived = useCallback(async () => {
    if (archivedSelectedRows.length === 0) return;
    const deletedIds = await props.onBulkDelete(archivedSelectedRows);
    if (deletedIds.length > 0) {
      setArchivedRowSelection((prev) => removeIdsFromRowSelection(prev, deletedIds));
    }
  }, [archivedSelectedRows, props]);

  const savedBulkActions =
    savedSelectedIds.length > 0 ? (
      <>
        <Button
          type="button"
          variant="outline-orange"
          size="sm"
          data-font="primary"
          className="shrink-0"
          disabled={props.bulkActionBusy}
          onClick={() => void runBulkArchive()}
        >
          <span className="normal-case tracking-normal">Archive selected</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-font="primary"
          data-trade-log-action="delete"
          className="shrink-0 !border-red-500 !text-red-500"
          disabled={props.bulkActionBusy}
          onClick={() => void runBulkDeleteSaved()}
        >
          <span className="normal-case tracking-normal">Delete selected</span>
        </Button>
      </>
    ) : null;

  const archivedBulkActions =
    archivedSelectedIds.length > 0 ? (
      <>
        <Button
          type="button"
          variant="outline-brand"
          size="sm"
          data-font="primary"
          className="shrink-0"
          disabled={props.bulkActionBusy}
          onClick={() => void runBulkRestore()}
        >
          <span className="normal-case tracking-normal">Restore selected</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-font="primary"
          data-trade-log-action="delete"
          className="shrink-0 !border-red-500 !text-red-500"
          disabled={props.bulkActionBusy}
          onClick={() => void runBulkDeleteArchived()}
        >
          <span className="normal-case tracking-normal">Delete selected</span>
        </Button>
      </>
    ) : null;

  const headerActions = (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
      {props.listTab === "saved" ? savedBulkActions : archivedBulkActions}
      <Button
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        className="shrink-0"
        onClick={props.onNewStrategy}
      >
        <span className="normal-case tracking-normal">Create new strategy</span>
      </Button>
    </div>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <div data-strategies-table="" className="w-full min-w-0">
      <Tabs
        data-trade-log-tabs=""
        className="w-full"
        value={props.listTab}
        onValueChange={(value) => handleListTabChange(value as StrategyListTab)}
      >
        <div
          data-strategies-table-header=""
          className="mb-4 flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
            <div className="overflow-x-auto overflow-y-hidden">
              <TabsList className="min-w-max">
                <TabsTrigger value="saved" title="Active strategies">
                  Saved
                </TabsTrigger>
                <TabsTrigger value="archived" title="Archived strategies">
                  Archived
                </TabsTrigger>
              </TabsList>
            </div>
            <Input
              value={props.query}
              onChange={(e) => props.onQueryChange(e.target.value)}
              placeholder="Search strategies…"
              data-strategies-table-search=""
              className={cn(
                TRADE_LOG_FILTER_INPUT_CLASS,
                "h-9 min-w-[200px] max-w-[420px] flex-1 normal-case tracking-normal",
              )}
              aria-label="Search strategies"
            />
          </div>
          <div className="flex shrink-0 items-center gap-2">{headerActions}</div>
        </div>

        <TabsContent value="saved" className="mt-0">
          <StrategyTablePanel
            mode="saved"
            rows={props.savedRows}
            onOpenStrategy={props.onOpenStrategy}
            columns={savedColumns}
            emptyMessage="No saved strategies found."
            rowSelection={savedRowSelection}
            onRowSelectionChange={setSavedRowSelection}
          />
        </TabsContent>

        <TabsContent value="archived" className="mt-0">
          <StrategyTablePanel
            mode="archived"
            rows={props.archivedRows}
            onOpenStrategy={props.onOpenStrategy}
            columns={archivedColumns}
            emptyMessage="No archived strategies found."
            rowSelection={archivedRowSelection}
            onRowSelectionChange={setArchivedRowSelection}
          />
        </TabsContent>
      </Tabs>
    </div>
    </TooltipProvider>
  );
}
